import { useState } from "react";
import { Form } from "antd";

interface UseEntityEditorOptions {
  item: Record<string, any> | null;
  updateFn: (input: Record<string, any>) => Promise<any>;
  onSuccess?: () => void;
  toFormValues?: (item: Record<string, any>) => Record<string, any>;
  fromFormValues?: (values: Record<string, any>) => Record<string, any>;
}

function useEntityEditor(options: UseEntityEditorOptions) {
  const { item, updateFn, onSuccess, toFormValues, fromFormValues } = options;
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  function startEditing() {
    const values = item && toFormValues ? toFormValues(item) : item;
    form.setFieldsValue(values);
    setSaveError(false);
    setEditing(true);
  }

  function cancelEditing() {
    form.resetFields();
    setSaveError(false);
    setEditing(false);
  }

  function save() {
    setSaveError(false);
    form.validateFields().then(() => {
      const dirtyValues = form.getFieldsValue(true, (meta) => meta.touched);

      if (Object.keys(dirtyValues).length === 0) {
        setEditing(false);
        form.resetFields();
        return;
      }

      setSaving(true);
      const transformed = fromFormValues ? fromFormValues(dirtyValues) : dirtyValues;

      updateFn({
        uid: item?.uid,
        rowVersion: item?.rowVersion,
        ...transformed,
      })
        .then(() => {
          setSaving(false);
          setEditing(false);
          form.resetFields();
          onSuccess?.();
        })
        .catch(() => {
          setSaving(false);
          setSaveError(true);
        });
    }).catch(() => {
      setSaveError(true);
    });
  }

  return { form, editing, saving, saveError, startEditing, cancelEditing, save };
}

export default useEntityEditor;
